import React, { useEffect, useState } from 'react';
import { useCases } from '../../utils/paymentUseCases';
import { PaymentTitleProps } from '../types/Payment';
import { useTranslations } from '../../localization/useTranslations';

const PaymentTitle: React.FC<PaymentTitleProps> = ({ status, code, payment_gateway, type, failed }) => {
  // State to track whether the component has mounted
  const [initialized, setInitialized] = useState(false);

  const translations = useTranslations();

  // Function to check visibility based on use cases
  const isVisible = (useCaseIds: number[]) => {
    return useCaseIds.some((id) => {
      const useCase = useCases.find((uc) => uc.id === id);
      return (
        useCase && useCase.code === code && useCase.payment_gateway.includes(payment_gateway) && useCase.type === type
      );
    });
  };

  const titles = [
    {
      text:
        translations?.paymentSummary?.paymentTitleSuccess ||
        'Thank you for your purchase! Your regular dose of inspiration is waiting.',
      isVisible: isVisible([1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33, 35, 38]),
    },
    {
      text:
        translations?.paymentSummary?.paymentTitleFirst ||
        "One step left - and you'll be able to enjoy the full benefits of subscribers.",
      isVisible: isVisible([2, 8, 14, 20, 22, 24, 26, 28, 30, 32, 34, 36]),
    },
    {
      text:
        translations?.paymentSummary?.paymentTitleSecond ||
        'One step left - and you open the door to a regular flow of inspiration.',
      isVisible: isVisible([4, 10, 16]),
    },
    {
      text:
        translations?.paymentSummary?.paymentTitleThird ||
        "One step away and you'll be able to enjoy Forbes Premium to the fullest.",
      isVisible: isVisible([6, 12, 18, 37]),
    },
  ];

  // Function to get the appropriate class modifier
  const getClassModifier = () => {
    switch (true) {
      case payment_gateway === 'bank_transfer' && status === 'form':
        return '--blue';
      case (payment_gateway === 'gopay' || payment_gateway === 'gopay_recurrent') && status === 'paid':
        return '--green';
      case status === 'fail' || status === 'timeout':
        return '--red';
      case (payment_gateway === 'gopay' || payment_gateway === 'gopay_recurrent') && status === 'form':
        return '--red';
      default:
        return '';
    }
  };

  const classModifier = getClassModifier();

  // Use useEffect to set initialized to true when the component mounts
  useEffect(() => {
    setInitialized(true);
  }, []);

  // Use useEffect to add and remove body classes
  useEffect(() => {
    if (initialized) {
      document.body.classList.add(`page${classModifier}-background`);
      return () => {
        document.body.classList.remove(`page${classModifier}-background`);
      };
    }
  }, [classModifier, initialized]);

  return (
    <div className={`page-sales-success__header page-sales-success__header${classModifier}`}>
      <div className="container">
        <h2 className="page-sales-success__title" id="headerTitle">
          {failed && (
            <React.Fragment
              key={translations?.paymentSummary?.paymentTitleFailed || 'Your payment could not be processed'}
            >
              {translations?.paymentSummary?.paymentTitleFailed || 'Your payment could not be processed'}
            </React.Fragment>
          )}

          {!failed &&
            titles
              .filter((title) => title.isVisible)
              .map((title) => <React.Fragment key={title.text}>{title.text}</React.Fragment>)}
        </h2>
      </div>
    </div>
  );
};

export default PaymentTitle;
